import { Component, Input, Output, EventEmitter, ContentChildren, QueryList, AfterContentInit } from '@angular/core';
import { AccordionPanelComponent } from './accordion-panel.component';

export type AccordionType = 'default' | 'flush' | 'bordered';

/**
 * Accordion Component
 *
 * A wrapper component for creating accordion/collapsible panels.
 *
 * Usage:
 * ```html
 * <!-- Basic accordion -->
 * <app-accordion>
 *   <app-accordion-panel id="panel1" title="Section 1">
 *     Content for section 1...
 *   </app-accordion-panel>
 *   <app-accordion-panel id="panel2" title="Section 2">
 *     Content for section 2...
 *   </app-accordion-panel>
 * </app-accordion>
 *
 * <!-- Always open (multiple panels can be open) -->
 * <app-accordion [closeOthers]="false">
 *   ...
 * </app-accordion>
 *
 * <!-- Flush style (no borders) -->
 * <app-accordion type="flush">
 *   ...
 * </app-accordion>
 *
 * <!-- With initially expanded panels -->
 * <app-accordion [activeIds]="['panel1']">
 *   ...
 * </app-accordion>
 * ```
 */
@Component({
  selector: 'app-accordion',
  standalone: false,
  templateUrl: './accordion.component.html',
  styleUrl: './accordion.component.scss'
})
export class AccordionComponent implements AfterContentInit {
  /** Accordion style: default, flush, or bordered */
  @Input() type: AccordionType = 'default';

  /** Whether only one panel can be open at a time */
  @Input() closeOthers = true;

  /** IDs of initially expanded panels */
  @Input() activeIds: string[] = [];

  /** Whether to animate the open/close transition */
  @Input() animation = true;

  /** Emits when a panel is opened or closed */
  @Output() panelChange = new EventEmitter<{ panelId: string; nextState: boolean }>();

  @ContentChildren(AccordionPanelComponent) panels!: QueryList<AccordionPanelComponent>;

  expandedPanels = new Set<string>();

  ngAfterContentInit(): void {
    // Set initially expanded panels
    this.activeIds.forEach(id => this.expandedPanels.add(id));

    // If no panels are specified and we have panels, expand the first one
    if (this.expandedPanels.size === 0 && this.panels.length > 0) {
      // Don't auto-expand any panel by default
    }
  }

  get accordionClass(): string {
    const classes: string[] = ['accordion'];

    if (this.type === 'flush') {
      classes.push('accordion-flush');
    } else if (this.type === 'bordered') {
      classes.push('accordion-bordered');
    }

    return classes.join(' ');
  }

  isExpanded(panelId: string): boolean {
    return this.expandedPanels.has(panelId);
  }

  toggle(panelId: string): void {
    const panel = this.panels.find(p => p.id === panelId);
    if (panel?.disabled) return;

    const isCurrentlyExpanded = this.isExpanded(panelId);

    if (this.closeOthers && !isCurrentlyExpanded) {
      // Close all other panels
      this.expandedPanels.clear();
    }

    if (isCurrentlyExpanded) {
      this.expandedPanels.delete(panelId);
    } else {
      this.expandedPanels.add(panelId);
    }

    this.panelChange.emit({ panelId, nextState: !isCurrentlyExpanded });
  }
}
